'use client'

// Global dataLayer
declare global {
  interface Window {
    // Optional: GTM populates it, so it may be undefined before the snippet loads.
    // Must match the declaration in components/cookies/utils.ts.
    dataLayer?: any[]
  }
}

// Shared Offer type
type Offer = {
  uuid?: string
  price?: string | number
  currency?: string
}

// User GTM Action types
type UserProps = {
  id?: string
  email?: string
}

type ContentProps = {
  id?: string
  name?: string
}

// Shared Data type
type Data = {
  event: string
  product?: string
  variant?: string
  user?: {
    email_address?: string
    id?: string
  }
  offer?: {
    id?: string
    price?: string | number
    currency?: string
  }
  step_number?: number
  step_name?: string
  event_id?: string
}

const pushToDataLayer = (data: Data) => {
  if (!window.dataLayer) {
    window.dataLayer = []
  }
  window.dataLayer.push(data)
}

// User GTM Action function
export const userGtmAction = ({
  user,
  product,
  action,
  content,
}: {
  user: UserProps
  product: string
  action: string
  content?: ContentProps
}) => {
  if (!window) return

  window?.dataLayer?.push({
    event: 'user_action',
    user: {
      id: user?.id,
      ...(user?.email && { email: user.email }),
    },
    product,
    action,
    content: {
      id: content?.id,
      ...(content?.name && { name: content.name }),
    },
    version: '2.0.0',
  })
}

// FUNNEL EVENTS
export const viewItem = ({ product, variant }: { product: string; variant?: string }) => {
  const data: Data = { event: 'view_item' }

  if (product) {
    data.product = product
  }
  if (variant) {
    data.variant = variant
  }

  pushToDataLayer(data)
}

export const shareInfo = ({ product, variant }: { product: string; variant?: string }) => {
  const data: Data = { event: 'share_info' }

  if (product) {
    data.product = product
  }

  if (variant) {
    data.variant = variant
  }

  pushToDataLayer(data)
}

export const generateLead = ({
  product,
  variant,
  email,
  user_id,
}: {
  product: string
  variant?: string
  email?: string
  user_id?: string
}) => {
  const data: Data = { event: 'generate_lead' }

  if (product) {
    data.product = product
  }
  if (variant) {
    data.variant = variant
  }
  if (email || user_id) {
    data.user = {}
    if (email) data.user.email_address = email
    if (user_id) data.user.id = user_id
  }

  pushToDataLayer(data)
}

export const addToCart = ({
  product,
  variant,
  email,
  user_id,
}: {
  product: string
  email?: string
  variant?: string
  user_id?: string
}) => {
  const data: Data = { event: 'add_to_cart' }

  if (product) {
    data.product = product
  }
  if (variant) {
    data.variant = variant
  }
  if (email || user_id) {
    data.user = {}
    if (email) data.user.email_address = email
    if (user_id) data.user.id = user_id
  }

  pushToDataLayer(data)
}

export const initiateCheckout = ({
  product,
  variant,
  email,
  offer,
  user_id,
}: {
  product?: string
  email?: string
  offer?: Offer
  variant?: string
  user_id?: string
}) => {
  const data: Data = { event: 'begin_checkout' }

  if (product) {
    data.product = product
  }
  if (variant) {
    data.variant = variant
  }
  if (email || user_id) {
    data.user = {}
    if (email) data.user.email_address = email
    if (user_id) data.user.id = user_id
  }
  if (offer) {
    data.offer = {
      id: offer.uuid,
      price: offer.price,
      currency: offer.currency || 'USD',
    }
  }

  pushToDataLayer(data)
}

export const purchase = ({
  product,
  variant,
  email,
  offer,
  user_id,
  event_id,
}: {
  product: string
  variant?: string
  email?: string
  offer?: Offer
  user_id?: string
  event_id?: string
}) => {
  const data: Data = { event: 'purchase' }

  if (event_id) {
    data.event_id = event_id
  }
  if (product) {
    data.product = product
  }
  if (variant) {
    data.variant = variant
  }
  if (email || user_id) {
    data.user = {}
    if (email) data.user.email_address = email
    if (user_id) data.user.id = user_id
  }
  if (offer) {
    data.offer = {
      id: offer.uuid,
      price: offer.price,
      currency: offer.currency || 'USD',
    }
  }

  pushToDataLayer(data)
}

export const funnelProgress = ({
  product,
  variant,
  step_name,
  step_number,
}: {
  product: string
  variant?: string
  step_name: string
  step_number: number
}) => {
  const data: Data = {
    event: 'funnel_progress',
    product,
    step_number,
    step_name,
  }
  if (variant) {
    data.variant = variant
  }

  pushToDataLayer(data)
}
